import { useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { ArrowLeft, ChevronRight, AlertTriangle, Loader2 } from 'lucide-react'
import { Button } from '@/core/ui/Button'
import { useIsAuthenticated, useSession } from '@/core/auth/SessionStore'
import { PickerSheet } from '@/features/common/PickerSheet'
import { useObrasPorProjeto } from '@/core/db/daos'
import { useMvesCarreteiroPorObra } from '@/features/carreteiro/useMvesCarreteiro'
import { useChefeObra, usePrecheckCarreteiro } from '@/features/carreteiro/useCabecalhoCarreteiro'
import { dataBR } from '@/features/carreteiro/carreteiroRegras'
import type { CatObra } from '@/core/db/entities'
import { cn } from '@/lib/cn'
import {
  isHeaderComplete,
  useCarreteiroStore,
  type TipoCarreteiro,
} from '@/features/carreteiro/carreteiroStore'

/**
 * Step 0: dados da ficha de carreteiro.
 *
 *  - Tipo R/U vem de cat_obra.tipoProjetoId (id=1 Urbana → 'U', id=2 Rodoviária → 'R').
 *  - Chefe da ficha = chefe do projeto da obra NA DATA (histórico do SIGA),
 *    não o chefe administrativo. Sem chefe na data → bloqueia com a mensagem do SIGA.
 *  - Caminhões = os que o chefe recebeu por transferência vigente na data,
 *    com contrato de carreteiro vigente (regra do SIGA web).
 *  - Precheck (online): medição gerada, transferência, retroativo e vigência
 *    são conferidos antes de lançar as viagens.
 */

type ValidationError = { code: 'NO_TIPO' | 'TIPO_INVALIDO'; msg: string }

function classifyObra(obra: CatObra): { tipo: TipoCarreteiro | null; error: ValidationError | null } {
  if (obra.tipoProjetoId == null) {
    return {
      tipo: null,
      error: {
        code: 'NO_TIPO',
        msg: 'Obra sem classificação (Urbana/Rodoviária) no SIGA. Atualize antes de prosseguir.',
      },
    }
  }
  // ID 1 = Pav. Urb., ID 2 = Pav. Rod. (confirmado por inspeção SIGA 2026-05-25).
  if (obra.tipoProjetoId === 1) return { tipo: 'U', error: null }
  if (obra.tipoProjetoId === 2) return { tipo: 'R', error: null }
  return {
    tipo: null,
    error: {
      code: 'TIPO_INVALIDO',
      msg: `Obra classificada como "${obra.tipoProjetoNome ?? '?'}" — fora do escopo do carreteiro (apenas Urbana e Rodoviária).`,
    },
  }
}

export function CarreteiroStep0Route() {
  const authed = useIsAuthenticated()
  const session = useSession()
  const navigate = useNavigate()

  const store = useCarreteiroStore()
  const setChefe = useCarreteiroStore((s) => s.setChefe)
  const projetoIdSessao = session?.projetoAtualId ?? null

  // Bootstrap: copia projetoId da sessão na primeira montagem para destravar pickers.
  useEffect(() => {
    if (store.projetoId == null && projetoIdSessao != null) {
      useCarreteiroStore.setState({ projetoId: projetoIdSessao })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projetoIdSessao])

  const obras = useObrasPorProjeto(store.projetoId)
  const [picker, setPicker] = useState<'obra' | 'mve' | null>(null)

  const obraSelecionada = useMemo<CatObra | null>(() => {
    if (store.obraId == null || !obras) return null
    return obras.find((o) => o.id === store.obraId) ?? null
  }, [store.obraId, obras])

  const validation = useMemo(
    () => (obraSelecionada ? classifyObra(obraSelecionada) : null),
    [obraSelecionada],
  )
  const obraValida = obraSelecionada != null && validation != null && validation.error == null

  // Chefe da ficha (histórico do projeto na data) → store
  const chefe = useChefeObra(obraValida ? store.obraId : null, store.dt)
  useEffect(() => {
    if ((chefe.status === 'live' || chefe.status === 'cache') && chefe.chefe) {
      setChefe(chefe.chefe.id, chefe.chefe.nome, chefe.status)
    } else if (chefe.status === 'sem_chefe') {
      setChefe(null, null, null)
    }
  }, [chefe, setChefe])

  const podeEscolherMve = obraValida && chefe.status !== 'sem_chefe' && chefe.status !== 'loading'
  const mveResult = useMvesCarreteiroPorObra(podeEscolherMve ? store.obraId : null, store.dt)
  const precheck = usePrecheckCarreteiro(podeEscolherMve ? store.obraId : null, store.mveId, store.dt)

  if (!authed) return <Navigate to="/login" replace />
  if (projetoIdSessao == null) return <Navigate to="/" replace />

  const completo =
    podeEscolherMve &&
    mveResult.semChefe == null &&
    isHeaderComplete(store) &&
    precheck.status !== 'bloqueado' &&
    precheck.status !== 'loading'

  return (
    <main className="min-h-svh bg-bg-base flex flex-col">
      <header className="sticky top-0 z-10 bg-bg-base/95 backdrop-blur border-b border-border pt-safe pl-safe pr-safe">
        <div className="flex items-center gap-2 px-2 py-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/carreteiro')} aria-label="Voltar">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-bold text-on-surface">Carreteiro · 1 de 3 · Ficha</h1>
        </div>
      </header>

      <div className="flex-1 p-4 space-y-3 max-w-2xl mx-auto w-full">
        <ReadOnlyField label="DATA" value={dataBR(store.dt)} />

        <PickerField
          label="OBRA"
          value={store.obraNome || 'Selecionar…'}
          badge={obraSelecionada && store.tipo ? (store.tipo === 'U' ? 'Urbana' : 'Rodoviária') : null}
          onClick={() => setPicker('obra')}
        />

        {validation?.error && <Alerta tone="warn">{validation.error.msg}</Alerta>}

        {/* Chefe de equipe da ficha — só leitura, vem do SIGA */}
        {obraValida && chefe.status === 'loading' && (
          <p className="text-xs text-on-surface-dim px-1 inline-flex items-center gap-2">
            <Loader2 className="h-3 w-3 animate-spin" /> Buscando chefe de equipe da obra…
          </p>
        )}
        {obraValida && chefe.status === 'sem_chefe' && <Alerta tone="bad">{chefe.message}</Alerta>}
        {obraValida && (chefe.status === 'live' || chefe.status === 'cache' || chefe.status === 'desconhecido') && (
          <>
            <ReadOnlyField label="CHEFE DE EQUIPE" value={chefe.chefe?.nome ?? '—'} />
            {chefe.message && <p className="text-xs text-warn px-1">{chefe.message}</p>}
          </>
        )}

        <PickerField
          label="CAMINHÃO/MVE"
          value={
            store.mvePrefixo
              ? `${store.mvePlaca ? `${store.mvePrefixo} · ${store.mvePlaca}` : store.mvePrefixo}${store.mveProprio ? ' · frota própria' : ''}`
              : 'Selecionar…'
          }
          onClick={() => {
            if (!podeEscolherMve) return
            setPicker('mve')
          }}
          disabled={!podeEscolherMve}
        />

        {store.mveId != null && store.mveProprio && (
          <p id="aviso-frota-propria" className="text-xs text-on-surface-dim px-1">
            Frota própria: as viagens entram na parte diária deste caminhão no dia. Se ela ainda não foi lançada, o servidor
            guarda a ficha e anexa sozinho. O número da ficha é gerado pelo servidor, como nos terceiros.
          </p>
        )}

        {/* Avisos do picker MVE */}
        {podeEscolherMve && mveResult.loading && (
          <p className="text-xs text-on-surface-dim px-1 inline-flex items-center gap-2">
            <Loader2 className="h-3 w-3 animate-spin" /> Buscando caminhões do chefe na data…
          </p>
        )}
        {podeEscolherMve && mveResult.semChefe && <Alerta tone="bad">{mveResult.semChefe}</Alerta>}
        {podeEscolherMve && !mveResult.loading && mveResult.source === 'cache' && (
          <p className="text-xs text-warn px-1">
            Sem rede — lista de caminhões em cache. Vínculo e contrato serão validados no envio.
          </p>
        )}
        {podeEscolherMve &&
          !mveResult.loading &&
          !mveResult.semChefe &&
          mveResult.source != null &&
          mveResult.mves.length === 0 && (
            <p className="text-xs text-on-surface-dim px-1">
              Nenhum caminhão vinculado ao chefe desta obra com contrato de carreteiro vigente em{' '}
              {dataBR(store.dt)}.
            </p>
          )}
        {podeEscolherMve && !mveResult.loading && mveResult.source == null && mveResult.error && (
          <p className="text-xs text-warn px-1">
            Sem rede e sem cache de caminhões desta obra. Conecte e tente novamente.
          </p>
        )}

        {/* Precheck das regras do SIGA para o caminhão escolhido */}
        {store.mveId != null && precheck.status === 'loading' && (
          <p className="text-xs text-on-surface-dim px-1 inline-flex items-center gap-2">
            <Loader2 className="h-3 w-3 animate-spin" /> Conferindo regras do SIGA…
          </p>
        )}
        {store.mveId != null && precheck.bloqueios.length > 0 && (
          <Alerta tone="bad">
            {precheck.bloqueios.map((b) => (
              <span key={b.code} className="block">
                {b.message}
              </span>
            ))}
          </Alerta>
        )}
        {store.mveId != null && precheck.avisos.length > 0 && (
          <Alerta tone="warn">
            {precheck.avisos.map((a) => (
              <span key={a} className="block">
                {a}
              </span>
            ))}
          </Alerta>
        )}
        {store.mveId != null && precheck.message && (
          <p className="text-xs text-warn px-1">{precheck.message}</p>
        )}

        {/* KM inicial/final ficam no step1 (Padrão do dia). */}
      </div>

      <div className="sticky bottom-0 bg-bg-base/95 backdrop-blur border-t border-border p-4 pb-safe-plus-4 pl-safe pr-safe">
        <Button
          variant="primary"
          size="lg"
          disabled={!completo}
          onClick={() => navigate('/carreteiro/step1')}
          className="w-full"
        >
          Continuar
        </Button>
      </div>

      {picker === 'obra' && (
        <PickerSheet
          open
          title="Selecionar obra"
          items={obras ?? []}
          label={(o) => o.nome}
          sublabel={(o) => o.codigo}
          onPick={(o) => {
            // Classifica já para definir o tipo. Obra inválida ainda fica
            // selecionada para mostrar a mensagem abaixo do picker.
            const result = classifyObra(o)
            store.setObra(o.id, o.nome, o.projetoId, result.tipo ?? 'R')
            setPicker(null)
          }}
          onDismiss={() => setPicker(null)}
          searchHint="Buscar obra"
          emptyMessage="Nenhuma obra ativa neste projeto"
        />
      )}
      {picker === 'mve' && (
        <PickerSheet
          open
          title="Caminhão / Veículo"
          items={mveResult.mves}
          // Prefixo + placa no topo (PickerSheet filtra em label+sublabel).
          label={(m) => {
            const prefixo = m.prefixo ?? '(sem prefixo)'
            return m.placa ? `${prefixo} · ${m.placa}` : prefixo
          }}
          sublabel={(m) => [m.proprio ? 'FROTA PRÓPRIA' : null, m.marca, m.modelo].filter(Boolean).join(' · ') || null}
          onPick={(m) => {
            store.setMve(m.id, m.prefixo, m.placa ?? null, m.proprio === true)
            setPicker(null)
          }}
          onDismiss={() => setPicker(null)}
          searchHint="Buscar por prefixo ou placa"
          emptyMessage={
            mveResult.loading
              ? 'Carregando caminhões…'
              : mveResult.source == null
                ? 'Sem rede e sem cache de caminhões. Conecte e tente novamente.'
                : 'Nenhum caminhão com contrato de carreteiro vigente para o chefe desta obra.'
          }
          header={
            mveResult.loading ? (
              <div className="px-4 py-2 text-xs text-on-surface-dim flex items-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin" /> Buscando caminhões…
              </div>
            ) : mveResult.source === 'cache' ? (
              <div className="px-4 py-2 text-xs text-warn">
                Sem rede — lista em cache. Vínculo e contrato serão validados no envio.
              </div>
            ) : null
          }
        />
      )}
    </main>
  )
}

function Alerta({ tone, children }: { tone: 'warn' | 'bad'; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        'rounded-lg border px-4 py-3 flex items-start gap-3',
        tone === 'bad' ? 'border-bad bg-bad/10' : 'border-warn bg-warn/10',
      )}
    >
      <AlertTriangle className={cn('h-5 w-5 shrink-0 mt-0.5', tone === 'bad' ? 'text-bad' : 'text-warn')} />
      <p className="text-sm text-on-surface">{children}</p>
    </div>
  )
}

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div className="w-full card-surface-variant px-4 py-3">
      <p className="text-xs font-medium text-on-surface-dim uppercase">{label}</p>
      <p className="text-on-surface mt-0.5 truncate">{value}</p>
    </div>
  )
}

function PickerField({
  label,
  value,
  badge,
  onClick,
  disabled,
}: {
  label: string
  value: string
  badge?: string | null
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full card-surface-variant text-left px-4 py-3 flex items-center gap-3 min-h-touch hover:bg-surface-2/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-xs font-medium text-on-surface-dim uppercase">{label}</p>
          {badge && (
            <span className="text-[10px] uppercase tracking-wide bg-accent/15 text-accent px-1.5 py-0.5 rounded">
              {badge}
            </span>
          )}
        </div>
        <p className="text-on-surface mt-0.5 truncate">{value}</p>
      </div>
      <ChevronRight className="h-5 w-5 text-on-surface-dim" />
    </button>
  )
}
